import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import MapboxView from './MapboxView';
import projectApi from '../../utils/api/project';
import uploadApi from '../../utils/api/upload';
import Loading from '../Shared/ResourceIndex/Loading';

const MIN_PX = 400;
const DEFAULT_W = 1200;
const DEFAULT_H = 800;

const toCoord = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);

  return Number.isFinite(n) ? n : null;
};

/** GIS Configure UI — Mapbox-based calibration view with toolbar, tooltip, loader, and a resizable capture rectangle (min 400x400) for gis_only site maps. When `embedded`, the parent supplies the header + toolbar; this component just renders the map area and exposes `save` via ref. */
const GISConfigure = forwardRef(function GISConfigure(
  {
    projectId,
    projectUpload,
    project,
    onSaved,
    onCancel,
    onReadyChange,
    embedded = false,
    bounds = null,
  },
  ref,
) {
  const isTabletOrLess = useMediaQuery({ maxWidth: 1024 });
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialCenter, setInitialCenter] = useState(null);
  const [noCoords, setNoCoords] = useState(false);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!project) return; // still loading
    const lat = toCoord(project?.address?.latitude);
    const lng = toCoord(project?.address?.longitude);

    if (lat != null && lng != null) {
      setInitialCenter([lng, lat]);
      setNoCoords(false);
    } else {
      setNoCoords(true);
    }
  }, [project]);

  useEffect(() => {
    return () => {
      if (onReadyChange) onReadyChange(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({ save: handleSave })); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMapLoad = useCallback((map) => {
    // Coordinate tooltip — DOM ref to avoid React re-renders on mousemove.
    map.on('mousemove', (e) => {
      const el = tooltipRef.current;
      if (!el) return;
      el.style.display = 'block';
      el.style.left = `${e.point.x + 14}px`;
      el.style.top = `${e.point.y + 14}px`;
      el.textContent = `${e.lngLat.lat.toFixed(6)}, ${e.lngLat.lng.toFixed(6)}`;
    });
    const hideTooltip = () => {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    };
    map.on('mouseleave', hideTooltip);
    map.getContainer().addEventListener('mouseleave', hideTooltip);

    setMapLoaded(true);
    if (onReadyChange) onReadyChange(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize the rect centered in the container once it's measured.
  useEffect(() => {
    if (!mapLoaded || rect) return;
    const el = containerRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    const w = Math.max(MIN_PX, Math.min(DEFAULT_W, width - 80));
    const h = Math.max(MIN_PX, Math.min(DEFAULT_H, height - 80));
    setRect({ x: (width - w) / 2, y: (height - h) / 2, w, h });
  }, [mapLoaded, rect]);

  // Drags a corner handle ('tl'|'tr'|'bl'|'br'); the opposite corner stays anchored.
  const startResize = (corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const px = e.clientX,
      py = e.clientY;
    const r0 = rect;
    const onMove = (ev) => {
      const dx = ev.clientX - px;
      const dy = ev.clientY - py;
      let x = r0.x,
        y = r0.y,
        w = r0.w,
        h = r0.h;
      if (corner[1] === 'l') {
        w = Math.max(MIN_PX, r0.w - dx);
        x = r0.x + (r0.w - w);
      }
      if (corner[1] === 'r') {
        w = Math.max(MIN_PX, r0.w + dx);
      }
      if (corner[0] === 't') {
        h = Math.max(MIN_PX, r0.h - dy);
        y = r0.y + (r0.h - h);
      }
      if (corner[0] === 'b') {
        h = Math.max(MIN_PX, r0.h + dy);
      }
      const c = containerRef.current?.getBoundingClientRect();
      if (c) {
        if (x < 0) {
          w += x;
          x = 0;
        }
        if (y < 0) {
          h += y;
          y = 0;
        }
        if (x + w > c.width) w = c.width - x;
        if (y + h > c.height) h = c.height - y;
      }
      setRect({ x, y, w, h });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleSave = async () => {
    if (!mapLoaded || saving) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    setSaving(true);
    try {
      // Force a synchronous render so the WebGL drawing buffer is current.
      map.triggerRepaint();
      await new Promise((resolve) => map.once('idle', resolve));

      if (!rect) throw new Error('Boundary not measured');

      const topLeft = map.unproject([rect.x, rect.y]);
      const topRight = map.unproject([rect.x + rect.w, rect.y]);
      const bottomRight = map.unproject([rect.x + rect.w, rect.y + rect.h]);
      const bottomLeft = map.unproject([rect.x, rect.y + rect.h]);

      const sourceCanvas = map.getCanvas();
      const dpr = sourceCanvas.width / sourceCanvas.clientWidth;
      const out = document.createElement('canvas');

      out.width = rect.w;
      out.height = rect.h;
      out
        .getContext('2d')
        .drawImage(
          sourceCanvas,
          rect.x * dpr,
          rect.y * dpr,
          rect.w * dpr,
          rect.h * dpr,
          0,
          0,
          rect.w,
          rect.h,
        );

      const blob = await new Promise((resolve) =>
        out.toBlob(resolve, 'image/png'),
      );

      if (!blob) throw new Error('Failed to capture screenshot');
      const previousFileName = projectUpload?.upload?.name || '';
      const file = new File(
        [blob],
        embedded
          ? previousFileName || `gis-site-map-${Date.now()}.png`
          : `gis-site-map-${Date.now()}.png`,
        {
          type: 'image/png',
        },
      );
      const uploaded = await uploadApi.create(file);

      if (!uploaded?.id) throw new Error('Upload failed');

      const linked = await projectApi.linkPendingSiteMap(
        projectId,
        projectUpload.id,
        {
          upload_id: uploaded.id,
          width: out.width,
          height: out.height,
          geo_top_left_lat: topLeft.lat,
          geo_top_left_lng: topLeft.lng,
          geo_top_right_lat: topRight.lat,
          geo_top_right_lng: topRight.lng,
          geo_bottom_left_lat: bottomLeft.lat,
          geo_bottom_left_lng: bottomLeft.lng,
          geo_bottom_right_lat: bottomRight.lat,
          geo_bottom_right_lng: bottomRight.lng,
        },
      );

      if (onSaved) onSaved(linked, uploaded);
    } catch (err) {
      console.error('GISConfigure save failed', err);
    } finally {
      setSaving(false);
    }
  };

  if (isTabletOrLess) {
    return (
      <div
        className='map-editor'
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {!embedded && (
          <>
            {/* Header — same dark bar as the map editor; only the project name is shown. */}
            <div className='map-editor-top'>
              <div className='map-editor-header'>
                <div className='map-editor-header-left'>
                  <h1>{project?.name}</h1>
                </div>
              </div>
            </div>

            {/* Toolbar — mirrors the GeoReference toolbar (prompt left, Cancel+Save right). */}
            <div className='map-editor-toolbar' style={{ width: '100%' }}>
              <div
                className='map-editor-toolbar-actions'
                style={{ justifyContent: 'flex-end' }}
              >
                <button
                  className='pure-button pure-button-primary'
                  onClick={onCancel}
                  disabled={saving}
                >
                  <span>Cancel</span>
                  <FontAwesomeIcon
                    className='map-editor-cancel-icon'
                    icon={faTimes}
                    style={{ marginLeft: 6 }}
                  />
                </button>
              </div>
            </div>
          </>
        )}
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            color: '#0d2330',
            fontSize: 16,
          }}
        >
          Use Desktop for Proper GIS Configuration
        </div>
      </div>
    );
  }

  if (noCoords) {
    return (
      <div
        className='gis-configure gis-configure--error'
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          gap: 12,
        }}
      >
        <p>
          This project does not have GPS coordinates set. Please update the
          project address with a valid latitude and longitude before
          configuring.
        </p>
        <button className='button' onClick={onCancel}>
          Close
        </button>
      </div>
    );
  }

  if (!initialCenter) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading what='map' />
      </div>
    );
  }

  return (
    <div
      className='map-editor'
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {!embedded && (
        <>
          {/* Header — same dark bar as the map editor; only the project name is shown. */}
          <div className='map-editor-top'>
            <div className='map-editor-header'>
              <div className='map-editor-header-left'>
                <h1>{project?.name}</h1>
              </div>
            </div>
          </div>

          {/* Toolbar — mirrors the GeoReference toolbar (prompt left, Cancel+Save right). */}
          <div className='map-editor-toolbar' style={{ width: '100%' }}>
            <div style={{ justifyContent: 'flex-start' }}>
              Pan &amp; Zoom to position the area you want to capture, then
              click "Save"
            </div>
            <div
              className='map-editor-toolbar-actions'
              style={{ justifyContent: 'flex-end' }}
            >
              <button
                className='pure-button pure-button-primary'
                onClick={onCancel}
                disabled={saving}
              >
                <span>Cancel</span>
                <FontAwesomeIcon
                  className='map-editor-cancel-icon'
                  icon={faTimes}
                  style={{ marginLeft: 6 }}
                />
              </button>
              <button
                className='pure-button pure-button-primary'
                onClick={handleSave}
                disabled={!mapLoaded || saving}
              >
                <span>{saving ? 'Saving…' : 'Save GeoReferencing'} </span>
                <FontAwesomeIcon
                  className='map-editor-save-icon'
                  icon={faSave}
                />
              </button>
            </div>
          </div>
        </>
      )}

      <div
        ref={containerRef}
        style={{ position: 'relative', flex: 1, minHeight: 0 }}
      >
        {/* Loader overlay — visible until the map is loaded. */}
        {!mapLoaded && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
            }}
          >
            <Loading what='map' />
          </div>
        )}

        <MapboxView
          ref={mapRef}
          onMapLoad={handleMapLoad}
          center={initialCenter}
          style='mapbox://styles/mapbox/satellite-streets-v12'
          preserveDrawingBuffer
          maxBounds={bounds}
        />

        {/* Live coordinate tooltip — DOM ref, no React re-renders. */}
        <div
          ref={tooltipRef}
          style={{
            display: 'none',
            position: 'absolute',
            zIndex: 10,
            background: 'rgba(0,0,0,0.65)',
            color: '#fff',
            fontSize: 11,
            fontFamily: 'monospace',
            padding: '3px 7px',
            borderRadius: 4,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            lineHeight: 1.5,
          }}
        />

        {/* Resizable capture rectangle — drag any of the 4 corner handles to resize. */}
        {rect && (
          <>
            <div
              style={{
                position: 'absolute',
                left: rect.x,
                top: rect.y,
                width: rect.w,
                height: rect.h,
                border: '2px solid #48a398',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
                zIndex: 6,
              }}
            />
            {['tl', 'tr', 'bl', 'br'].map((c) => (
              <div
                key={c}
                onPointerDown={startResize(c)}
                style={{
                  position: 'absolute',
                  left: c[1] === 'l' ? rect.x - 7 : rect.x + rect.w - 7,
                  top: c[0] === 't' ? rect.y - 7 : rect.y + rect.h - 7,
                  width: 14,
                  height: 14,
                  background: '#fff',
                  border: '2px solid #48a398',
                  borderRadius: 2,
                  cursor:
                    c === 'tl' || c === 'br' ? 'nwse-resize' : 'nesw-resize',
                  zIndex: 7,
                  touchAction: 'none',
                }}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
});

export default GISConfigure;
